import { useEffect, useRef } from 'react';
import { mapEngine } from '../../map-engine/MapEngineService';
import { useNetworkEditSafe } from '../contexts/NetworkEditContext';
import { useUrbanNetworkSafe } from '../UrbanNetworkContext';

interface UseCanonicalEdgeSemanticHoverConfig {
  enabled: boolean;
}

function dispatchSemanticHover(elementId: string | null, clientX = 0, clientY = 0): void {
  window.dispatchEvent(new CustomEvent('network-edges-hover', {
    detail: { elementId, clientX, clientY },
  }));
}

export function useCanonicalEdgeSemanticHover({ enabled }: UseCanonicalEdgeSemanticHoverConfig): void {
  const urbanNetwork = useUrbanNetworkSafe();
  const networkEdit = useNetworkEditSafe();
  const pointerPositionRef = useRef<{ clientX: number; clientY: number }>({ clientX: 0, clientY: 0 });
  const hoveredEdgeIdRef = useRef<string | null>(urbanNetwork?.hoveredEdgeId ?? null);
  const editModeRef = useRef(networkEdit?.editMode ?? null);

  useEffect(() => {
    hoveredEdgeIdRef.current = urbanNetwork?.hoveredEdgeId ?? null;
    editModeRef.current = networkEdit?.editMode ?? null;
  }, [networkEdit?.editMode, urbanNetwork?.hoveredEdgeId]);

  useEffect(() => {
    if (!enabled) {
      dispatchSemanticHover(null);
      return;
    }

    let attachedCanvas: HTMLCanvasElement | null = null;
    let pollInterval: number | null = null;

    const handlePointerMove = (event: MouseEvent): void => {
      pointerPositionRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
      };

      if (!hoveredEdgeIdRef.current || editModeRef.current === 'create-branch') {
        return;
      }

      dispatchSemanticHover(hoveredEdgeIdRef.current, event.clientX, event.clientY);
    };

    const handlePointerLeave = (): void => {
      dispatchSemanticHover(null);
    };

    const attachListeners = (): boolean => {
      const map = mapEngine.getMap();
      if (!map) {
        return false;
      }

      const canvas = map.getCanvas();
      if (attachedCanvas === canvas) {
        return true;
      }

      canvas.addEventListener('mousemove', handlePointerMove);
      canvas.addEventListener('mouseleave', handlePointerLeave);
      attachedCanvas = canvas;
      return true;
    };

    if (!attachListeners()) {
      pollInterval = window.setInterval(() => {
        if (attachListeners() && pollInterval) {
          window.clearInterval(pollInterval);
          pollInterval = null;
        }
      }, 300);
    }

    return () => {
      if (pollInterval) {
        window.clearInterval(pollInterval);
      }
      if (attachedCanvas) {
        attachedCanvas.removeEventListener('mousemove', handlePointerMove);
        attachedCanvas.removeEventListener('mouseleave', handlePointerLeave);
      }
      dispatchSemanticHover(null);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || networkEdit?.editMode === 'create-branch' || !urbanNetwork?.hoveredEdgeId) {
      dispatchSemanticHover(null);
      return;
    }

    const { clientX, clientY } = pointerPositionRef.current;
    dispatchSemanticHover(urbanNetwork.hoveredEdgeId, clientX, clientY);
  }, [enabled, networkEdit?.editMode, urbanNetwork?.hoveredEdgeId]);
}
